/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {Link, List, ListItem, makeStyles, withStyles} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {IconButton} from '../../../components/buttons/IconButton';
import {MapEditIcon as CurrentMapEditIcon} from '../../../icons/current/MapEditIcon';
import {SystemDeleteIcon} from '../../../icons/current/SystemDeleteIcon';
import {SystemFavouriteDefaultIcon} from '../../../icons/current/SystemFavouriteDefaultIcon';
import {SystemFavouriteFilledIcon} from '../../../icons/current/SystemFavouriteFilledIcon';
import {DeleteIcon} from '../../../icons/DeleteIcon';
import {MapEditIcon} from '../../../icons/MapEditIcon';
import {StarsFillIcon} from '../../../icons/StarsFillIcon';
import {StarsIcon} from '../../../icons/StarsIcon';
import {dlsAlt, dlsColor, dlsTypography} from '../../../theme/theme';
import useThemeContext from '../../../theme/useThemeContext';
import {Color, DLSTheme, IconSize, SavedMapViewType} from '../../../types/types';
import {shortenName} from './SavedMapViewsChooser';

interface MapViewListItemProps {
  mapView: SavedMapViewType;
  savedMapViews?: SavedMapViewType[];
  onSetMapView?: Function;
  onSetDefaultMapView?: Function;
  onEdit?: Function;
  onDelete?: Function;
  isTracking?: boolean;
};

const useStyles = makeStyles(theme => ({
  itemPointerEvents : {
    pointerEvents : 'auto'
  },
  cursor : {
    cursor : 'pointer'
  },
  mapViewItemContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'inherit',
    width          : '100%',
    height         : '100%'
  },
  mapViewTitle : {
    display    : 'flex',
    alignItems : 'center',
    width      : '25%'
  },
  mapViewTitleText : {
    ...dlsTypography('body1'),
    display      : 'flex',
    width        : '100%',
    wordWrap     : 'break-word',
    marginLeft   : 12,
    textOverflow : 'ellipsis'
  },
  container : {
    display       : 'flex',
    flexDirection : 'column',
    width         : '65%'
  },
  mapViewActions : {
    display : 'flex'
  }
}));

const StyledListItem = withStyles(theme => ({
  root : {
    justifyContent : 'space-between',
    '&$selected'   : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')}`
    }
  },
  selected : {}
}))(ListItem);

const StartIcon = memo(({isDefault}: {isDefault: boolean}): JSX.Element => {
  return isDefault
    ? <StarsFillIcon size={IconSize.small} color={dlsColor('caBlue')} />
    : <StarsIcon size={IconSize.small} color={dlsColor('caBlue')} />;
});

// the current theme's list item icons: edit, delete and a favourite heart
// for the default view, filled red once it's the default. The heart's
// artwork only fills about 14px of its 24px box, so it's drawn at 28px to
// read the same size as the edit and delete icons ...
const getCurrentIcons = (isDefault: boolean) => ({
  edit     : <CurrentMapEditIcon size={IconSize.medium} color={dlsColor('grey1')} />,
  delete   : <SystemDeleteIcon size={IconSize.medium} color={dlsColor('grey1')} />,
  favorite : isDefault
    ? <SystemFavouriteFilledIcon size={IconSize.mediumLarge} color={dlsColor('red')} />
    : <SystemFavouriteDefaultIcon size={IconSize.mediumLarge} color={dlsColor('grey1')} />
});

const MapViewListItem = ({
  isTracking,
  mapView,
  savedMapViews,
  onSetMapView,
  onSetDefaultMapView,
  onEdit,
  onDelete
}: MapViewListItemProps) => {
  const classes     = useStyles();
  const isPhone     = useIsPhone();
  const {isCurrent} = useThemeContext();
  const getBasemapURL = (basemap: string) => (`assets/img/basemaps/${basemap}.jpg`);
  const {isDefault}   = mapView || {};
  const defaultTitle  = translate(`maps:controls.savedMapViews.${isDefault ? 'disabled' : 'enabled'}`);
  const currentIcons  = isCurrent && getCurrentIcons(Boolean(isDefault));
  const buttonColor   = dlsColor(isCurrent ? 'grey1' : 'caBlue');
  // the current IconButton recolors its icon with its own color, so the
  // default view's heart button is red to keep the heart filled red ...
  const favoriteColor = isCurrent && isDefault ? dlsColor('red') : buttonColor;

  const onEditClick = useCallback((e) => {
    onEdit?.(mapView);
    e.stopPropagation();
  }, [mapView, onEdit]);

  const onDeleteClick = useCallback((e) => {
    onDelete?.(mapView, savedMapViews);
    e.stopPropagation();
  }, [mapView, savedMapViews, onDelete]);

  const onSelect = useCallback((e) => {
    onSetMapView?.(mapView);
    e.stopPropagation();
  }, [mapView, onSetMapView]);

  const onSetDefaultClick = useCallback((e) => {
    const {isDefault} = mapView || {};
    onSetDefaultMapView?.(!isDefault, mapView);
    e.stopPropagation();
  }, [mapView, onSetDefaultMapView]);

  return (
    <StyledListItem disableRipple
      className={classnames(classes.cursor, classes.itemPointerEvents)}
      button
      onClick={onSelect}>
      <div className={classes.mapViewItemContainer}>
        <div id='left' className={classes.mapViewTitle}>
          <img width={80} src={getBasemapURL(mapView?.basemap)} />
        </div>
        <div className={classes.container}>
          <div className={classes.mapViewTitleText}>
            {shortenName(mapView?.id)}
          </div>
          <div className={classes.mapViewActions}>
            <Tooltip title={translate('maps:controls.savedMapViews.edit')} renderOnlyChildren={isPhone} arrow>
              <IconButton onClick={onEditClick} icon={currentIcons ? currentIcons.edit : <MapEditIcon size={IconSize.small} color={dlsColor('caBlue')}/>} color={buttonColor} />
            </Tooltip>
            <Tooltip title={translate('maps:controls.savedMapViews.delete')} renderOnlyChildren={isPhone} arrow>
              <IconButton onClick={onDeleteClick} icon={currentIcons ? currentIcons.delete : <DeleteIcon size={IconSize.small} color={dlsColor('caBlue')}/>} color={buttonColor} />
            </Tooltip>
            {isTracking &&
            <Tooltip title={defaultTitle} renderOnlyChildren={isPhone} arrow>
              <IconButton onClick={onSetDefaultClick}
                icon={currentIcons ? currentIcons.favorite : <StartIcon isDefault={Boolean(isDefault)} />}
                color={favoriteColor} />
            </Tooltip>}
          </div>
        </div>
      </div>
    </StyledListItem>
  );
};

export default MapViewListItem;
